import BigNumber from 'bignumber.js'
import * as DateF from 'date-fns'

import { Array, Iterable, Order, pipe } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { MathUtil } from '@root/shared'

import type { UserSummaryCreateData } from '@blade/trade-api-client'
import type { WalletId } from '@root/entities/wallet'

export type Summary = {
  network: Network.Network
  totalSummary: BigNumber
  timestamp: Date
  byWallet: Record<string, { totalSummary: BigNumber }>
}

export const fromDto = (dto: UserSummaryCreateData[number]): Summary => {
  return {
    network: Network.fromDto(dto.network),
    timestamp: new Date(dto.timestamp),
    totalSummary: BigNumber(dto.totalSummary || 0),
    byWallet: pipe(
      dto.wallets,
      Iterable.toRecord(
        (it) => it.walletId,
        (it) => ({ totalSummary: BigNumber(it.totalSummary) }),
      ),
    ),
  }
}

const getTimestamp = (sum: Summary) => sum.timestamp

export const cmpByTimestamp = Order.mapInput(Order.Date, getTimestamp)
export const cmpByTimestampDesc = Order.flip(cmpByTimestamp)

export const createWalletSummaries = (
  walletId: WalletId,
  summaries: Array<Summary>,
): Array<Summary> => {
  const result: Array<Summary> = []

  for (const summaryItem of summaries) {
    const walletSummary = summaryItem.byWallet[walletId]
    if (!walletSummary) {
      continue
    }

    const item: Summary = {
      byWallet: {},
      network: summaryItem.network,
      timestamp: summaryItem.timestamp,
      totalSummary: walletSummary.totalSummary,
    }
    result.push(item)
  }

  return result
}

export type DataEntry = [Date, BigNumber]

export const createChartData = (
  summaries: Array<Summary>,
  today: Date,
): Array<DataEntry> | undefined => {
  const lastSummary = summaries.at(-1)

  if (!lastSummary) {
    return undefined
  }

  const oldestBoundDay = DateF.subDays(today, 29)

  const relevantSummaries = summaries.filter((it) =>
    DateF.isAfter(it.timestamp, DateF.startOfDay(oldestBoundDay)),
  )

  const result = pipe(
    Iterable.range(0, 29),
    Iterable.map((it): DataEntry | undefined => {
      const day = DateF.startOfDay(DateF.subDays(today, it))

      const summaryEntry = relevantSummaries.find((sum) =>
        DateF.isSameDay(sum.timestamp, day),
      )
      if (!summaryEntry) {
        return undefined
      }
      return [day, summaryEntry.totalSummary]
    }),
    Iterable.filter((it) => it !== undefined),
    Iterable.toArray,
    Array.reverse,
  )

  if (result.length === 0) {
    return undefined
  }

  return result
}

export const calculateSummaryPriceChange = (
  summaries: Array<Summary>,
): { percentage: number; absolute: BigNumber } | undefined => {
  const latestEntry = summaries.at(-1)
  const previousEntry = summaries.at(-2)
  if (!previousEntry || !latestEntry) {
    return undefined
  }
  if (previousEntry.totalSummary === latestEntry.totalSummary) {
    return undefined
  }
  const percentageChange = MathUtil.percentageChangeBn(
    previousEntry.totalSummary,
    latestEntry.totalSummary,
  )

  return {
    absolute: latestEntry.totalSummary.minus(previousEntry.totalSummary),
    percentage: percentageChange.toNumber(),
  }
}
